<script setup lang="ts">
import { computed, nextTick, ref, Ref, watch } from 'vue';
import { Dayjs } from 'dayjs';
import FeedDateSelectbox from '@/components/common/feed-date-selectbox.vue';
import {
  createPersonalNewsShouldEnable,
  useAutoGenerateSummary,
} from '@/components/feed-summary/composables/use-auto-generate-summary';
import { usePersonalFeedSummary } from '@/components/feed-summary/composables/use-personal-feed-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummary from '@/components/feed-summary/feed-summary.vue';
import ArticlesExecution from '@/components/personal-news/articles-execution.vue';
import { Article, Execution, FeedType, ThemeFeedType } from '@/types';
import {
  executionInformationForDate,
  isAfternoonFeedEnabled,
} from '@/utils/personalNews';

export type FeedData = {
  articles: Article[];
  isLoaded: boolean;
  isFetchingData: boolean;
  load: () => void;
};

export type MultipleExecutionFeedData = {
  morning: FeedData;
  afternoon: FeedData;
};

export type DateArticles = {
  date: Dayjs;
  dateStr: string;
  dateLabel: string;
  domestic: Ref<MultipleExecutionFeedData>;
  foreign: Ref<MultipleExecutionFeedData>;
};

type Props = {
  dateArticles: DateArticles;
  sentFirstPersonalNewsFeedAt?: Dayjs;
  removedArticleIds: number[];
  displayFeedType: FeedType;
  dateOptions: { label: string; value: string }[];
  previousFeedCount: { domestic: number; foreign: number };
  isFirst?: boolean;
};
const props = defineProps<Props>();

const emit = defineEmits<{
  'select-date': [value: string];
}>();

const domesticMorningArticles = computed(() =>
  props.dateArticles.domestic.value.morning.articles.filter(
    a => !props.removedArticleIds.includes(a.id),
  ),
);

const domesticAfternoonArticles = computed(() =>
  props.dateArticles.domestic.value.afternoon.articles.filter(
    a => !props.removedArticleIds.includes(a.id),
  ),
);

const foreignMorningArticles = computed(() =>
  props.dateArticles.foreign.value.morning.articles.filter(
    a => !props.removedArticleIds.includes(a.id),
  ),
);

const foreignAfternoonArticles = computed(() =>
  props.dateArticles.foreign.value.afternoon.articles.filter(
    a => !props.removedArticleIds.includes(a.id),
  ),
);

const articleCountDomestic = computed(
  () =>
    domesticMorningArticles.value.length +
    domesticAfternoonArticles.value.length,
);

const articleCountForeign = computed(
  () =>
    foreignMorningArticles.value.length + foreignAfternoonArticles.value.length,
);

const currentDate = ref(props.dateArticles.dateStr);
const selectDate = (value: string | undefined) => {
  if (value === undefined) return;

  emit('select-date', value);

  // セレクトボックスの表示を強制的に戻すために、
  // 選択された値を代入してから元の値を入れ直して変更検知を走らせる
  currentDate.value = value;
  nextTick(() => {
    currentDate.value = props.dateArticles.dateStr;
  });
};

const previousTotalFeedCount = (
  feedType: ThemeFeedType,
  execution: Execution,
) => {
  let previousTotal = props.previousFeedCount[feedType];
  if (execution === 'morning') {
    previousTotal +=
      props.dateArticles[feedType].value.afternoon.articles.length;
  }
  return previousTotal;
};

const executionInfoMorning = computed(() =>
  executionInformationForDate(
    props.dateArticles.date,
    'morning',
    props.sentFirstPersonalNewsFeedAt,
  ),
);
const executionInfoAfternoon = computed(() =>
  executionInformationForDate(
    props.dateArticles.date,
    'afternoon',
    props.sentFirstPersonalNewsFeedAt,
  ),
);

const shouldShowAfternoonFeed = computed(
  () =>
    (isAfternoonFeedEnabled(props.dateArticles.date) ||
      executionInfoAfternoon.value.isFirstExecution) &&
    !executionInfoMorning.value.isFirstExecution,
);

const shouldShowMorningFeed = computed(
  () => !executionInfoAfternoon.value.isFirstExecution,
);

const articleCount = computed(() => {
  if (props.displayFeedType === 'domestic') {
    return articleCountDomestic.value;
  } else if (props.displayFeedType === 'foreign') {
    return articleCountForeign.value;
  }
  return articleCountDomestic.value + articleCountForeign.value;
});

const {
  summary,
  generateButtonState,
  generateSummary,
  shouldShowSummary,
  summarySuffix,
} = usePersonalFeedSummary(
  {
    page_type: 'feed',
    feed_type: props.displayFeedType,
    doc_type: 'article',
    date: props.dateArticles.dateStr,
    execution: isAfternoonFeedEnabled(props.dateArticles.date)
      ? 'afternoon'
      : 'morning',
  },
  articleCount,
  'personal_news',
);

// URLにsummarizeパラメータがあったら自動で要約生成する
// isFirstのとき画面遷移時にも自動生成する
const { autoGenerationReason } = useAutoGenerateSummary(
  generateButtonState,
  generateSummary,
  'personal_feed' as const,
  createPersonalNewsShouldEnable({
    dateStr: props.dateArticles.dateStr,
    isFirst: props.isFirst,
  }),
);

// 自動生成が有効なとき、先頭でなければスクロールする
watch(autoGenerationReason, autoGenReason => {
  if (autoGenReason === undefined) return;
  const index = props.dateOptions.findIndex(
    option => option.value === props.dateArticles.dateStr,
  );
  if (index > 0) {
    emit('select-date', props.dateArticles.dateStr);
  }
});
</script>

<template>
  <div class="articles-day">
    <div class="date-header">
      <FeedDateSelectbox
        :options="dateOptions"
        :model-value="currentDate"
        @update:model-value="selectDate"
      />
      <FeedSummaryGenerationButton
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <hr class="header-divider" />
    <FeedSummary
      v-if="shouldShowSummary(summary)"
      :feed-summary="summary"
      :suffix="summarySuffix"
      feature="personal_feed"
      page-name="personal_news"
      class="feed-summary"
    />
    <div
      v-if="displayFeedType === 'domestic' || displayFeedType === 'all'"
      class="feed-articles-media"
    >
      <div class="media-title-name c-title-bold--m">
        国内メディア
        <span class="media-title-count c-text-regular--s"
          >・{{ articleCountDomestic }}件</span
        >
      </div>
      <template v-if="shouldShowAfternoonFeed">
        <ArticlesExecution
          :execution-info="executionInfoAfternoon"
          :articles="domesticAfternoonArticles"
          :removed-article-ids="removedArticleIds"
          :previous-total-feed-count="
            previousTotalFeedCount('domestic', 'afternoon')
          "
          :is-loaded="dateArticles.domestic.value.afternoon.isLoaded"
          :is-fetching-data="
            dateArticles.domestic.value.afternoon.isFetchingData
          "
          :theme-feed-type="'domestic'"
        />
      </template>
      <template v-if="shouldShowMorningFeed">
        <ArticlesExecution
          :execution-info="executionInfoMorning"
          :articles="domesticMorningArticles"
          :removed-article-ids="removedArticleIds"
          :previous-total-feed-count="
            previousTotalFeedCount('domestic', 'morning')
          "
          :is-loaded="dateArticles.domestic.value.morning.isLoaded"
          :is-fetching-data="dateArticles.domestic.value.morning.isFetchingData"
          :theme-feed-type="'domestic'"
        />
      </template>
    </div>

    <div
      v-if="displayFeedType === 'foreign' || displayFeedType === 'all'"
      class="feed-articles-media"
    >
      <div class="media-title-name c-title-bold--m">
        海外メディア
        <span class="media-title-count c-text-regular--s"
          >・{{ articleCountForeign }}件</span
        >
      </div>
      <template v-if="shouldShowAfternoonFeed">
        <ArticlesExecution
          :execution-info="executionInfoAfternoon"
          :articles="foreignAfternoonArticles"
          :removed-article-ids="removedArticleIds"
          :previous-total-feed-count="
            previousTotalFeedCount('foreign', 'afternoon')
          "
          :is-loaded="dateArticles.foreign.value.afternoon.isLoaded"
          :is-fetching-data="
            dateArticles.foreign.value.afternoon.isFetchingData
          "
          :theme-feed-type="'foreign'"
        />
      </template>
      <template v-if="shouldShowMorningFeed">
        <ArticlesExecution
          :execution-info="executionInfoMorning"
          :articles="foreignMorningArticles"
          :removed-article-ids="removedArticleIds"
          :previous-total-feed-count="
            previousTotalFeedCount('foreign', 'morning')
          "
          :is-loaded="dateArticles.foreign.value.morning.isLoaded"
          :is-fetching-data="dateArticles.foreign.value.morning.isFetchingData"
          :theme-feed-type="'foreign'"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.articles-day {
  background: $color-white;
  border-radius: 4px;
}

.feed-articles-media {
  position: relative;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 0;
  padding: 0 16px 20px;
}
.media-title-count {
  color: $color-gray600;
}

.date-header {
  position: sticky;
  top: var(--layout-sticky-top-under-tabs, 112px);
  z-index: 1;

  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 16px;
  background: $color-white;
  border-radius: 4px;

  .generation-button {
    margin-left: auto;
  }
}

.feed-summary {
  margin: 0 12px 16px;
}

.header-divider {
  border: none;
  border-top: 1px solid $color-gray200;
  padding-bottom: 16px;
  background: $color-white;
}
</style>
